import {
  DESCRIPTION_KEY,
  EPIC_ID,
  EPIC_ID_PREFIX,
  TASK_ID_PREFIX,
  Epic,
  ID_KEY,
  STORY_ID_PREFIX,
  SUMMARY_KEY
} from '../common';
import {
  EpicWithStoryCollection,
  EpicsMap,
  EpicsWithStoriesResponse,
  IssueResponseMap,
  StoriesAndTasksResponse,
  EpicsWithTasksResponse,
  SingleEpicWithTasksResponse,
  SingleStoryWithTasksResponse,
  StoriesMap,
  Task,
  Story
} from '../common/Types';
import { ChatGptMessage, ChatGptRole } from '../services/StoryGeneratorService/ChatGpt.types';

export const formatChatGPTMessage = (
  message: string,
  values: string[],
  type: ChatGptRole
): ChatGptMessage => {
  let formattedMessage = message;
  values.forEach((key, index) => {
    formattedMessage = formattedMessage.replace(`{${index}}`, key);
  });
  return {
    role: type,
    content: formattedMessage
  };
};

export const validateAutoGeneratedIdsForEpicWithStory = (
  epicWithStoryCollection: EpicWithStoryCollection,
  epicIncrementId: number,
  storyIncrementId: number
): EpicWithStoryCollection => {
  if (epicWithStoryCollection.stories && Object.keys(epicWithStoryCollection.stories).length > 0) {
    const firstStoryIdInCollection = getIdKeyFromString(
      Object.keys(epicWithStoryCollection.stories)[0]
    );
    if (storyIncrementId !== firstStoryIdInCollection) {
      epicWithStoryCollection = {
        ...epicWithStoryCollection,
        stories: updateIdsWithIncrementalNumber<Story>(
          epicWithStoryCollection.stories,
          STORY_ID_PREFIX,
          storyIncrementId
        )
      };
    }
  }

  if (epicWithStoryCollection.epics && Object.keys(epicWithStoryCollection.epics).length > 0) {
    const firstEpicIdInCollection = getIdKeyFromString(
      Object.keys(epicWithStoryCollection.epics)[0]
    );
    if (firstEpicIdInCollection === epicIncrementId) return epicWithStoryCollection;
    return updateIdsWithIncrementalNumberAndChild(
      epicWithStoryCollection,
      epicIncrementId,
      firstEpicIdInCollection
    );
  }
  return epicWithStoryCollection;
};

export const formatCollectionWithValidId = <T extends Story | Epic | Task>(
  collection: IssueResponseMap,
  prefixKey: string,
  nextIncrementId: number,
  additionalProps: Record<string, string> = {}
): Record<string, T> => {
  const updatedCollection: Record<string, T> = {};
  let newId = nextIncrementId;
  Object.keys(collection).forEach((key: string) => {
    const oldItem = collection[key];
    const newItem = {
      [ID_KEY]: `${prefixKey}_${newId++}`,
      [SUMMARY_KEY]: oldItem.s,
      [DESCRIPTION_KEY]: oldItem.d,
      ...additionalProps
    } as T;
    updatedCollection[newItem[ID_KEY]] = newItem;
  });

  return updatedCollection;
};

export const formatEpicsWithTasks = (
  collection: EpicsWithTasksResponse,
  epicIncrementId: number,
  taskIncrementId: number
): EpicWithStoryCollection => {
  const updatedEpics: EpicsMap = {};
  let updatedTasks: StoriesMap = {};
  let newEpicId = epicIncrementId;
  let newTaskId = taskIncrementId;
  const oldEpics = collection.epics || {};

  Object.keys(oldEpics).forEach((epicKey: string) => {
    const oldEpicItem = oldEpics[epicKey];
    const newEpicItem = {
      [ID_KEY]: `${EPIC_ID_PREFIX}_${newEpicId++}`,
      [SUMMARY_KEY]: oldEpicItem.s,
      [DESCRIPTION_KEY]: oldEpicItem.d
    };
    updatedEpics[newEpicItem[ID_KEY]] = newEpicItem;

    const oldEpicTasks = oldEpicItem.tasks || {};
    const tasks: StoriesMap = formatCollectionWithValidId<Task>(
      oldEpicTasks,
      TASK_ID_PREFIX,
      newTaskId,
      {
        [EPIC_ID]: newEpicItem[ID_KEY]
      }
    );
    newTaskId += Object.keys(tasks).length;
    updatedTasks = { ...updatedTasks, ...tasks };
  });

  return { epics: updatedEpics, stories: updatedTasks };
};

export const formatStoriesWithTasksAsEpics = (
  collection: StoriesAndTasksResponse,
  epicIncrementId: number,
  taskIncrementId: number
): EpicWithStoryCollection => {
  const updatedEpics: EpicsMap = {};
  let updatedTasks: StoriesMap = {};
  let newEpicId = epicIncrementId;
  let newTaskId = taskIncrementId;
  const oldStories = collection.stories || {};

  Object.keys(oldStories).forEach((storyKey: string) => {
    const oldStoryItem = oldStories[storyKey];
    const newEpicItem = {
      [ID_KEY]: `${EPIC_ID_PREFIX}_${newEpicId++}`,
      [SUMMARY_KEY]: oldStoryItem.s,
      [DESCRIPTION_KEY]: oldStoryItem.d
    };
    updatedEpics[newEpicItem[ID_KEY]] = newEpicItem;

    const oldStoryTasks = oldStoryItem.tasks || {};
    const tasks: StoriesMap = formatCollectionWithValidId<Task>(
      oldStoryTasks,
      TASK_ID_PREFIX,
      newTaskId,
      {
        [EPIC_ID]: newEpicItem[ID_KEY]
      }
    );
    newTaskId += Object.keys(tasks).length;
    updatedTasks = { ...updatedTasks, ...tasks };
  });

  return { epics: updatedEpics, stories: updatedTasks };
};

export const formatSingleEpicWithTasks = (
  collection: SingleEpicWithTasksResponse,
  epicIncrementId: number,
  taskIncrementId: number
): EpicWithStoryCollection => {
  const epicId = `${EPIC_ID_PREFIX}_${epicIncrementId}`;
  const epic = collection.epic ?? ({} as { s?: string; d?: string });
  const epics: EpicsMap = {
    [epicId]: {
      [ID_KEY]: epicId,
      [SUMMARY_KEY]: epic.s,
      [DESCRIPTION_KEY]: epic.d
    } as Epic
  };

  const tasks = formatCollectionWithValidId<Task>(
    collection.tasks || {},
    TASK_ID_PREFIX,
    taskIncrementId,
    { [EPIC_ID]: epicId }
  );

  return { epics, stories: tasks };
};

export const formatSingleStoryWithTasksAsEpic = (
  collection: SingleStoryWithTasksResponse,
  epicIncrementId: number,
  taskIncrementId: number
): EpicWithStoryCollection => {
  const epicId = `${EPIC_ID_PREFIX}_${epicIncrementId}`;
  const story = collection.story ?? ({} as { s?: string; d?: string });
  const epics: EpicsMap = {
    [epicId]: {
      [ID_KEY]: epicId,
      [SUMMARY_KEY]: story.s,
      [DESCRIPTION_KEY]: story.d
    } as Epic
  };

  const tasks = formatCollectionWithValidId<Task>(
    collection.tasks || {},
    TASK_ID_PREFIX,
    taskIncrementId,
    { [EPIC_ID]: epicId }
  );

  return { epics, stories: tasks };
};

export const formatEpicsWithStories = (
  collection: EpicsWithStoriesResponse,
  epicIncrementId: number,
  storyIncrementId: number
): EpicWithStoryCollection => {
  const updatedEpics: EpicsMap = {};
  let updatedStories: StoriesMap = {};
  let newEpicId = epicIncrementId;
  let newStoryId = storyIncrementId;
  const oldEpics = collection.epics || {};
  Object.keys(collection.epics).forEach((epicKey: string) => {
    const oldEpicItem = oldEpics[epicKey];
    const newEpicItem = {
      [ID_KEY]: `${EPIC_ID_PREFIX}_${newEpicId++}`,
      [SUMMARY_KEY]: oldEpicItem.s,
      [DESCRIPTION_KEY]: oldEpicItem.d
    };
    updatedEpics[newEpicItem[ID_KEY]] = newEpicItem;
    const oldEpicStories = oldEpicItem.stories || {};
    const stories: StoriesMap = formatCollectionWithValidId<Story>(
      oldEpicStories,
      STORY_ID_PREFIX,
      newStoryId,
      {
        [EPIC_ID]: newEpicItem[ID_KEY]
      }
    );
    newStoryId += Object.keys(stories).length;
    updatedStories = { ...updatedStories, ...stories };
  });

  return { epics: updatedEpics, stories: updatedStories };
};

const getIdKeyFromString = (key: string): number => {
  const match = key.match(/_(\d+)$/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return 0;
};

export const updateIdsWithIncrementalNumber = <T>(
  collection: Record<string, T>,
  prefixKey: string,
  nextIncrementId: number
): Record<string, T> => {
  const firstIdInCollection = getIdKeyFromString(Object.keys(collection)[0]);
  if (firstIdInCollection === nextIncrementId) return collection;
  const collectionObject: Record<string, T> = {};
  for (let i = 0; i < Object.keys(collection).length; i++) {
    const newId = `${prefixKey}_${nextIncrementId + i}`;
    const oldId = `${prefixKey}_${firstIdInCollection + i}`;

    if (Object.prototype.hasOwnProperty.call(collection, oldId)) {
      const data = collection[oldId];
      collectionObject[newId] = { ...data, id: newId };
    }
  }
  return collectionObject;
};

const updateIdsWithIncrementalNumberAndChild = (
  epicWithStoryCollection: EpicWithStoryCollection,
  updatedNumber: number,
  oldNumber: number
): EpicWithStoryCollection => {
  const epicCollection: Record<string, Epic> = {};

  for (let i = 0; i < Object.keys(epicWithStoryCollection.epics).length; i++) {
    const newId = `${EPIC_ID_PREFIX}_${updatedNumber + i}`;
    const oldId = `${EPIC_ID_PREFIX}_${oldNumber + i}`;

    if (Object.prototype.hasOwnProperty.call(epicWithStoryCollection.epics, oldId)) {
      const epic = epicWithStoryCollection.epics[oldId];
      epicCollection[newId] = { ...epic, id: newId };
    }

    Object.entries(epicWithStoryCollection.stories).forEach(([storyId, story]) => {
      if (story.epic_id === oldId) {
        epicWithStoryCollection.stories[storyId].epic_id = newId;
      }
    });
  }
  return { ...epicWithStoryCollection, epics: epicCollection };
};
